// src/pages/ForgotPassword.js
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccessibility } from '../contexts/AccessibilityContext';
import AacButton from '../components/aac/AacButton';
import './ForgotPassword.css';
import { useFocusableNavigation } from '../hooks/useFocusableNavigation';

const ForgotPassword = () => {
  const { resetPasswordForEmail } = useAuth();
  const { announce } = useAccessibility();
  useFocusableNavigation();

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const confirmationHeadingRef = useRef(null);

  useEffect(() => {
    if (sent && confirmationHeadingRef.current) {
      confirmationHeadingRef.current.focus();
    }
  }, [sent]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    const { error: resetError } = await resetPasswordForEmail(email);

    setLoading(false);

    if (resetError) {
      const message = 'Something went wrong sending the reset email. Please try again.';
      setError(message);
      announce(message);
      return;
    }

    setSent(true);
    announce('Password reset email sent');
  };

  // The submit button is type="submit" so a real click/Enter/Space already
  // triggers the form's onSubmit natively (required so Enter-in-any-field
  // still submits the form for switch/AAC users). Only the digit-1 AAC
  // shortcut needs to call handleSubmit directly, since it never produces a
  // real click and so never triggers native form submission on its own.
  const handleShortcutSubmit = (event) => {
    if (event.type === 'click') return;
    handleSubmit(event);
  };

  if (sent) {
    return (
      <div className="auth-page">
        <h1 ref={confirmationHeadingRef} tabIndex={-1}>Check Your Email</h1>
        <p className="auth-page__intro">
          If an account exists for {email}, we've sent a password reset link to it.
          Follow the link to choose a new password.
        </p>
        <div className="auth-page__links">
          <Link to="/login">Back to Log In</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <h1>Forgot Password</h1>
      <p className="auth-page__intro">Enter your email and we'll send you a link to reset your password.</p>

      <form className="auth-page__form" onSubmit={handleSubmit}>
        <label htmlFor="forgot-email">Email</label>
        <input
          id="forgot-email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
          autoComplete="email"
        />

        {error && <p className="auth-page__error" role="alert">{error}</p>}

        <div className="auth-page__actions">
          <AacButton type="submit" shortcut={1} onClick={handleShortcutSubmit} disabled={loading}>
            {loading ? 'Sending…' : 'Send Reset Link'}
          </AacButton>
        </div>
      </form>

      <div className="auth-page__links">
        <Link to="/login">Back to Log In</Link>
      </div>
    </div>
  );
};

export default ForgotPassword;
